import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getQuizV2AttemptDetails } from '@/features/Quiz/api/getQuizzes';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import Icon from '@/components/Icon';
import type { QuizV2QuestionResponseItem } from '@/features/Quiz/types';
import { QuizMathJaxProvider, QuizMathContent } from '@/components/ReactMathJax';
import { QuizHtml } from '@/features/Quiz/components/QuizHtml';
import { collectQuizItemMathText, hashMathText } from '@/features/Quiz/utils/mathText';

function formatLearnerResponse(raw: string | null | undefined): string {
    if (raw == null || raw === '') return '—';
    try {
        const j = JSON.parse(raw) as unknown;
        return typeof j === 'object' && j !== null ? JSON.stringify(j, null, 2) : String(j);
    } catch {
        return raw;
    }
}

function QuestionBlock({ q, index }: Readonly<{ q: QuizV2QuestionResponseItem; index: number }>) {
    const correct = q.correct === true;
    const wrong = q.correct === false;
    const mathText = collectQuizItemMathText({
        questionContentSnapshot: q.questionContentSnapshot,
        textOptions: (q.correctAnswerOptions ?? []).map((opt) => ({ text: opt.content })),
    });

    return (
        <Box
            p={'21px 35px 19px 16px'}
            borderRadius={'6px'}
            border={'1px solid'}
            borderColor="#e5ebf3"
            boxShadow={'0px 3px 10px 2px rgb(0 0 0 / 12%)'}
            m={'15px 20px'}
            bg={'white'}
            fontWeight="bold">
            <QuizMathContent mathText={mathText} mathKey={`${q.questionId ?? index}-${hashMathText(mathText)}`}>
                <Box color={'#F7B500'} fontSize={'11px'} pb={'7px'} fontWeight="700" lineHeight="13px" textTransform={'uppercase'}>
                    Question {index + 1}
                    {q.questionType ? ` · ${q.questionType}` : ''}
                </Box>
                {q.questionContentSnapshot ? (
                    <Box fontSize={'18px'} fontWeight="500" lineHeight="23px" color={'#202020'}>
                        <QuizHtml html={q.questionContentSnapshot} />
                    </Box>
                ) : (
                    <Text fontSize="14px" color="#617180" fontWeight="500">
                        (Question content unavailable)
                    </Text>
                )}

                {wrong || correct ? (
                    <Box color={correct ? '#3AD5A0' : '#F93F5E'} pt={'15px'} fontSize={'18px'}>
                        <Flex alignItems={'stretch'}>
                            <Icon color={correct ? '#3AD5A0' : '#F93F5E'} icon={correct ? 'circle-check' : 'circle-xmark'} />
                            <Box pl={'15px'}>{correct ? 'Correct' : 'Incorrect'}</Box>
                        </Flex>
                    </Box>
                ) : null}

                <Box pt={3} fontSize="14px" fontWeight="500" color="#202020">
                    <Text color="#617180" fontSize="12px" mb={1}>
                        Learner response
                    </Text>
                    <Box
                        as="pre"
                        whiteSpace="pre-wrap"
                        fontFamily="inherit"
                        fontWeight="400"
                        p={3}
                        bg="#f7fafc"
                        borderRadius="md"
                        border="1px solid #e5ebf3">
                        {formatLearnerResponse(q.learnerResponseJson)}
                    </Box>
                </Box>

                {q.correctAnswerOptions && q.correctAnswerOptions.length > 0 ? (
                    <Box pt={3} fontSize="14px" fontWeight="500">
                        <Text color="#0A9B8D" fontSize="12px" mb={1}>
                            Correct answer options
                        </Text>
                        {q.correctAnswerOptions.map((opt, i) => (
                            <Text key={`${opt.optionId ?? i}`} fontWeight="400">
                                {opt.sortIndex != null ? `${opt.sortIndex + 1}. ` : ''}
                                <QuizHtml html={opt.content ?? ''} as="span" />
                            </Text>
                        ))}
                    </Box>
                ) : null}
            </QuizMathContent>
        </Box>
    );
}

export const CustomerAttemptDetailsV2 = () => {
    const { attemptUuid } = useParams();
    const navigate = useNavigate();

    const { data, isLoading } = useQuery(
        ['quiz-v2-attempt-details', attemptUuid],
        () => (attemptUuid ? getQuizV2AttemptDetails(attemptUuid) : null),
        {
            enabled: !!attemptUuid,
            useErrorBoundary: (error: { response?: { status?: number } }) => !!error.response?.status,
        }
    );

    const displayName = useMemo(() => {
        if (data?.user?.email) return data.user.email;
        const u = data?.user;
        if (u?.firstName || u?.lastName) return [u.firstName, u.lastName].filter(Boolean).join(' ');
        return '—';
    }, [data?.user]);

    const pct = useMemo(() => {
        const p = data?.aggregatedTotals?.scorePercentage ?? data?.attempt?.scorePercentage;
        if (p == null) return null;
        return Number(p).toFixed(1);
    }, [data?.aggregatedTotals?.scorePercentage, data?.attempt?.scorePercentage]);

    if (isLoading) return <Spinner size="lg" />;

    const submitted = data?.attempt?.submittedAt;

    return (
        <Flex w="100%" flexDir="column" pb={'30px'}>
            <Box
                m={'20px 5px 0 5px'}
                bg={'#fff'}
                w={'99.5%'}
                h={'auto'}
                justifyContent={'space-between'}
                alignItems={'center'}
                borderRadius={'6px'}>
                <Flex
                    p={'25px 15px'}
                    bg={'#fff'}
                    color={'#000'}
                    w={'100%'}
                    h={'auto'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    borderTopLeftRadius={'6px'}
                    borderTopRightRadius={'6px'}
                    fontSize="13px"
                    fontWeight="500"
                    lineHeight="15px">
                    <Flex alignItems={'center'} fontSize={'18px'}>
                        <Button variant="back" onClick={() => navigate(-1)} leftIcon={<Icon icon="chevron-left" />} />
                        <Box>Quiz attempt — {displayName}</Box>
                    </Flex>
                </Flex>
                <Box bg={'#fff'} color={'#000'}>
                    <Box fontSize="14px" lineHeight="15px" fontWeight="500" borderBottom={'1px solid #e5ebf3'} p={'15px'}>
                        {data?.assignment?.quizTitle ?? ''}
                    </Box>
                    <Flex p={'15px'} flexWrap="wrap" gap={4}>
                        <Box w={'25%'} minW="200px" borderRight={'1px solid #e5ebf3'} pr={'15px'}>
                            <Box>Date completed</Box>
                            <Box fontSize={'16px'} fontWeight={'bold'}>
                                {submitted ? format(new Date(submitted), 'MMMM do, yyyy') : '—'}
                            </Box>
                        </Box>
                        <Box w={'15%'} minW="120px" borderRight={'1px solid'} borderRightColor="#e5ebf3" p={'0 15px'}>
                            <Box>Score %</Box>
                            <Box
                                fontSize={'16px'}
                                color={pct != null && Number(pct) < 60 ? '#d90000' : '#45d4a1'}
                                fontWeight={'bold'}>
                                {pct != null ? `${pct}%` : '—'}
                            </Box>
                        </Box>
                        <Box w={'40%'} minW="200px" p={'0 15px'}>
                            <Box>Evaluation</Box>
                            <Box fontSize={'16px'} fontWeight={'bold'}>
                                {data?.quizEvaluationType ?? '—'}
                            </Box>
                        </Box>
                    </Flex>
                </Box>

                <QuizMathJaxProvider>
                    {(data?.questionResponses ?? []).map((q, index) => (
                        <QuestionBlock key={q.questionId ?? index} q={q} index={index} />
                    ))}
                </QuizMathJaxProvider>
            </Box>
        </Flex>
    );
};
